import React, { useEffect } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Reveal } from "../components/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Button } from "../components/ui/button";
import { Link } from "react-router-dom";
import { HelpCircle, Users, ClipboardList, Video, Pill, ArrowRight, Phone, Shield } from "lucide-react";

const faqCategories = [
  {
    id: "about",
    icon: <HelpCircle className="w-6 h-6" />,
    label: "About Integro",
    color: "bg-[#005eb8]",
    faqs: [
      {
        q: "What is Integro Clinics?",
        a: "Integro Clinics is a UK-based specialist medical cannabis clinic. We provide safe, legal access to medical cannabis treatment through secure online video consultations and nationwide medication delivery. All prescriptions are issued by GMC-registered specialist clinicians following a thorough clinical review process.",
      },
      {
        q: "Who is the service for?",
        a: "Our service is for adults aged 21 and over who have a diagnosed medical condition that has not responded adequately to at least two conventional treatments. We do not offer a general GP service and cannot treat all conditions. Please use our eligibility checker to find out whether you may be suitable.",
      },
      {
        q: "Is Integro Clinics regulated?",
        a: "Yes. Integro Clinics is CQC (Care Quality Commission) registered and all our prescribing clinicians are registered with the General Medical Council (GMC). Medical cannabis prescriptions in the UK are issued under strict regulatory guidelines.",
      },
      {
        q: "Do I need a GP referral?",
        a: "No. You can apply directly through our eligibility checker without a GP referral. However, we will require access to your relevant medical records and documentation to support your clinical assessment.",
      },
    ],
  },
  {
    id: "eligibility",
    icon: <Shield className="w-6 h-6" />,
    label: "Eligibility",
    color: "bg-[#2ba78c]",
    faqs: [
      {
        q: "How do I check my eligibility?",
        a: "Visit our eligibility checker and complete a short questionnaire. It takes less than 30 seconds. Based on your responses, we will let you know whether you may be suitable to proceed with an application. Completing the eligibility check does not commit you to anything.",
      },
      {
        q: "What are the eligibility criteria?",
        a: "To be considered for treatment you generally need to: (1) be aged 21 or over; (2) have a diagnosed chronic medical condition; (3) have tried at least two conventional treatments without sufficient benefit or experienced unacceptable side effects; (4) not be pregnant; and (5) have no recent history of serious cardiovascular events. Each case is assessed individually by our clinical team.",
      },
      {
        q: "What conditions may be considered?",
        a: "We consider a range of conditions including: pain conditions such as back pain, fibromyalgia, arthritis, and cancer-related pain; neurological conditions such as migraine, multiple sclerosis, and Parkinson's disease; women's health conditions such as endometriosis, menopause, and PMDD; psychiatric and neurodevelopmental conditions such as PTSD, autism, and ADHD; and sleep difficulties including insomnia. This list is not exhaustive — please complete the eligibility check for your specific situation.",
      },
      {
        q: "What happens if I am not eligible?",
        a: "If our eligibility checker indicates that you may not be suitable, we will explain why. We would encourage you to speak with your GP about alternative treatment options. Eligibility decisions are made on clinical grounds only.",
      },
      {
        q: "Can I apply if I have a pre-existing mental health condition?",
        a: "This depends on your specific diagnosis and history. Some mental health conditions are considered in our assessments, while a history of psychosis may affect eligibility. Our clinical team reviews all applications individually. Please complete the eligibility check and our team will advise you.",
      },
    ],
  },
  {
    id: "journey",
    icon: <ClipboardList className="w-6 h-6" />,
    label: "Your Patient Journey",
    color: "bg-[#76c042]",
    faqs: [
      {
        q: "What happens after I check my eligibility?",
        a: "If your eligibility check suggests you may be suitable, you will be invited to create a secure patient account on our portal. From there, you can begin completing your patient profile with your medical history and relevant documents.",
      },
      {
        q: "How do I create a patient account?",
        a: "After completing the eligibility check, you will be directed to register on our secure patient portal. You will need to provide basic personal details and verify your identity. Account registration does not incur a charge.",
      },
      {
        q: "What information do I need to provide?",
        a: "You will need to provide: your personal details and contact information; a valid form of ID; details of your diagnosed condition(s); your current and previous medications; a summary of treatments you have already tried; and, where possible, supporting medical documentation such as GP letters or clinic correspondence.",
      },
      {
        q: "How do I complete my patient profile?",
        a: "Once logged into the patient portal, you will be guided through completing your profile step by step. Our team is available to help if you have any questions during this process. A complete profile helps our clinicians assess your case as thoroughly as possible.",
      },
      {
        q: "What medical records or documents might I need?",
        a: "Useful documents include: a summary of your medical history from your GP; specialist letters or clinic correspondence relating to your condition; details of medications you have been prescribed; and any relevant test results or diagnoses. We will guide you on exactly what is needed for your specific case.",
      },
    ],
  },
  {
    id: "consultation",
    icon: <Video className="w-6 h-6" />,
    label: "Consultations",
    color: "bg-[#00aeef]",
    faqs: [
      {
        q: "What happens during the initial consultation?",
        a: "Your initial consultation takes place via a secure video call with one of our specialist clinicians. They will review your medical history, discuss your symptoms and how they affect your daily life, explain the range of treatment options that may be appropriate, and answer any questions you have about medical cannabis.",
      },
      {
        q: "How long does the consultation take?",
        a: "Initial consultations typically last between 15 and 20 minutes.",
      },
      {
        q: "What should I prepare before my consultation?",
        a: "Before your appointment, we recommend: reviewing your medical history and making notes about your symptoms; having details of all current and previous medications to hand; preparing a list of questions you want to ask; and ensuring you are in a private, quiet location with a reliable internet connection.",
      },
      {
        q: "What happens after the consultation?",
        a: "After your consultation, your case is reviewed by our multi-disciplinary team (MDT). The MDT considers all clinical information before making a recommendation on whether treatment is appropriate. You will be informed of the outcome of this review.",
      },
      {
        q: "How much does a consultation cost?",
        a: "Clinical consultations, follow-up appointments, and repeat prescription reviews are provided at no charge. A £4.99 flat fee applies per prescription. There are no other charges.",
      },
    ],
  },
  {
    id: "prescriptions",
    icon: <Pill className="w-6 h-6" />,
    label: "MDT Review & Prescriptions",
    color: "bg-[#012d5c]",
    faqs: [
      {
        q: "What is an MDT review?",
        a: "A Multi-Disciplinary Team (MDT) review is a clinical process in which your case is assessed by a group of specialist clinicians with relevant expertise. This team approach ensures that prescribing decisions are made carefully, responsibly, and in the best interests of the patient.",
      },
      {
        q: "Who decides whether a prescription can be issued?",
        a: "The decision to issue a medical cannabis prescription rests with the MDT following a clinical review. No single clinician makes this decision in isolation. The MDT considers your full medical history, the outcome of your consultation, and all relevant clinical guidelines.",
      },
      {
        q: "Is a prescription guaranteed after my consultation?",
        a: "No. A prescription is not guaranteed. Issuing a medical cannabis prescription is a clinical decision made only after MDT review and approval. Not all patients who complete a consultation will receive a prescription. Our clinicians will always act in your best clinical interest.",
      },
      {
        q: "What happens if the MDT does not approve treatment?",
        a: "If the MDT determines that medical cannabis treatment is not appropriate for you at this time, our team will explain the reasons and discuss what alternative options may be available. We may recommend you return to your GP or explore other treatment pathways.",
      },
      {
        q: "How are prescriptions issued?",
        a: "Where treatment is approved by the MDT, a prescription is issued electronically by your clinician. Your medication is then dispensed by a specialist pharmacy and delivered discreetly to your door via secure nationwide delivery.",
      },
      {
        q: "How do repeat prescriptions work?",
        a: "Repeat prescriptions are managed through your patient portal. When a repeat is due, you will be notified to attend a follow-up consultation. Our team will review your progress and, if appropriate, issue a further prescription. Repeat prescription reviews are conducted as part of your ongoing care.",
      },
    ],
  },
  {
    id: "transfer",
    icon: <ArrowRight className="w-6 h-6" />,
    label: "Transferring Your Care",
    color: "bg-[#6b6be0]",
    faqs: [
      {
        q: "What is the Transfer Clinic service?",
        a: "The Transfer Clinic service allows patients who are currently receiving medical cannabis treatment at another UK clinic to move their care to Integro Clinics. We aim to make the transfer process as straightforward as possible.",
      },
      {
        q: "Who can use the Transfer Clinic service?",
        a: "The service is for existing medical cannabis patients who are currently under the care of another licensed UK clinic and wish to transfer to Integro. You must still meet our standard eligibility criteria.",
      },
      {
        q: "What information is needed from my existing clinic?",
        a: "You will typically need to provide: your current diagnosis and prescription details; a letter or summary from your existing clinic; details of your current treatment regimen; and any relevant medical correspondence. Our team will guide you through exactly what is required for your case.",
      },
      {
        q: "What happens to my existing prescription during the transfer?",
        a: "Your existing prescription remains with your current provider until your care has formally transferred to Integro. We will work to minimise any interruption to your treatment, but we cannot guarantee continuity of your exact current prescription as our MDT will review your case independently.",
      },
      {
        q: "How long does a transfer take?",
        a: "Transfer timescales vary depending on the complexity of each case and how quickly documentation is received from your existing clinic. Our team will keep you informed throughout the process.",
      },
    ],
  },
  {
    id: "practical",
    icon: <Users className="w-6 h-6" />,
    label: "Practical Questions",
    color: "bg-[#4caf50]",
    faqs: [
      {
        q: "How is my patient information handled?",
        a: "Your data is processed in accordance with UK GDPR and NHS data protection standards. All patient information is stored securely and is only accessed by authorised clinical and administrative staff involved in your care. We never sell or share your data with third parties for marketing purposes.",
      },
      {
        q: "Can existing patients continue using the service?",
        a: "Yes. Existing patients can continue managing their care through the patient portal, including booking follow-up appointments and requesting repeat prescriptions. All repeat prescriptions are subject to the same clinical review process.",
      },
      {
        q: "How do I contact the clinic?",
        a: "You can contact us by email at office@integroclinics.com. Our administrative team will respond as promptly as possible during working hours.",
      },
      {
        q: "What happens if I need help between appointments?",
        a: "For non-urgent queries, please contact our admin team via email. Our clinical team does not provide out-of-hours medical advice. If you have an urgent medical concern, please contact your GP or call NHS 111.",
      },
      {
        q: "What should I do in an emergency?",
        a: "Integro Clinics does not provide emergency medical services. In an emergency, please call 999. For urgent but non-emergency situations, please call NHS 111 or contact your GP. Do not delay seeking emergency care to contact our clinic.",
      },
      {
        q: "Can I use medical cannabis alongside my other medications?",
        a: "This is something our clinicians will assess carefully during your consultation. It is important to disclose all current medications during your application so that our team can consider any potential interactions. Never stop or change existing prescribed medications without speaking to your GP first.",
      },
    ],
  },
];

const FAQPage = () => {
  useEffect(() => {
    document.title = "FAQs | Integro Clinics";

    // FAQ structured data for SEO
    const allFaqs = faqCategories.flatMap((cat) =>
      cat.faqs.map((faq) => ({
        "@type": "Question",
        name: faq.q,
        acceptedAnswer: { "@type": "Answer", text: faq.a },
      }))
    );
    const schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: allFaqs,
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.innerHTML = JSON.stringify(schema);
    script.id = "faq-schema";
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById("faq-schema");
      if (el) document.head.removeChild(el);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white font-sans">
      <Navbar />

      {/* Hero */}
      <div
        className="bg-[#012d5c] py-20 lg:py-28"
        style={{
          backgroundImage: "linear-gradient(rgba(1,45,92,0.88),rgba(1,45,92,0.88)),url('https://images.pexels.com/photos/4031818/pexels-photo-4031818.jpeg?auto=compress&cs=tinysrgb&w=1920&h=900&fit=crop')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="container mx-auto px-4 text-center">
          <Reveal>
            <span className="inline-block bg-[#4caf50] text-white text-sm font-bold uppercase tracking-wider px-4 py-1.5 rounded-full mb-6">
              Help Centre
            </span>
            <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6">
              Frequently Asked Questions
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed mb-10">
              Everything you need to know about Integro Clinics and your patient journey — from eligibility to prescription.
            </p>
            <Button
              className="bg-[#4caf50] hover:bg-[#3d8b40] text-white px-8 py-6 rounded-lg text-lg font-bold shadow-lg"
              onClick={() => window.location.href = "https://app.integroclinics.com/v2/registration"}
            >
              Check Your Eligibility
            </Button>
          </Reveal>
        </div>
      </div>

      {/* Category Quick Nav */}
      <div className="bg-white border-b border-gray-100 py-6 sticky top-0 z-40 shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex gap-3 overflow-x-auto pb-1 scrollbar-hide">
            {faqCategories.map((cat) => (
              <a
                key={cat.id}
                href={`#${cat.id}`}
                className="flex items-center gap-2 whitespace-nowrap bg-[#f0f4f8] hover:bg-[#e0f2fe] text-[#005eb8] text-sm font-semibold px-4 py-2 rounded-full border border-gray-200 transition-colors shrink-0"
              >
                {cat.label}
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* FAQ Sections */}
      <div className="py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          {faqCategories.map((cat, catIdx) => (
            <section key={cat.id} id={cat.id} className="mb-16 scroll-mt-24">
              <Reveal>
                <div className="flex items-center gap-4 mb-8">
                  <div className={`${cat.color} w-12 h-12 rounded-xl flex items-center justify-center text-white shadow-md`}>
                    {cat.icon}
                  </div>
                  <h2 className="text-2xl lg:text-3xl font-bold text-[#012d5c]">
                    {cat.label}
                  </h2>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  <Accordion type="single" collapsible className="w-full">
                    {cat.faqs.map((faq, idx) => (
                      <AccordionItem
                        key={idx}
                        value={`${cat.id}-${idx}`}
                        className="border-b border-gray-100 last:border-0"
                      >
                        <AccordionTrigger className="text-base font-semibold text-[#005eb8] hover:text-[#012d5c] text-left px-6 py-5">
                          {faq.q}
                        </AccordionTrigger>
                        <AccordionContent className="text-gray-600 leading-relaxed text-base px-6 pb-5">
                          {faq.a}
                          {/* Internal link hints */}
                          {cat.id === "eligibility" && idx === 2 && (
                            <span className="block mt-3">
                              <Link to="/conditions/back-pain" className="text-[#4caf50] hover:underline font-medium">
                                View all conditions →
                              </Link>
                            </span>
                          )}
                          {cat.id === "transfer" && idx === 0 && (
                            <span className="block mt-3">
                              <Link to="/transfer-clinic" className="text-[#4caf50] hover:underline font-medium">
                                Learn more about the Transfer Clinic service →
                              </Link>
                            </span>
                          )}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              </Reveal>
            </section>
          ))}
        </div>
      </div>

      {/* Still have questions CTA */}
      <div className="bg-[#f4f7fa] py-20">
        <div className="container mx-auto px-4 max-w-3xl text-center">
          <Reveal>
            <h2 className="text-3xl font-bold text-[#012d5c] mb-4">Still have questions?</h2>
            <p className="text-lg text-gray-600 mb-8">
              Our team is here to help. Get in touch or start your eligibility check today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                className="bg-[#4caf50] hover:bg-[#3d8b40] text-white px-8 py-6 rounded-lg text-lg font-bold"
                onClick={() => window.location.href = "https://app.integroclinics.com/v2/registration"}
              >
                Check Eligibility
              </Button>
              <Button
                variant="outline"
                className="border-[#005eb8] text-[#005eb8] hover:bg-[#e0f2fe] px-8 py-6 rounded-lg text-lg"
                onClick={() => window.location.href = "mailto:office@integroclinics.com"}
              >
                Contact Us
              </Button>
            </div>
          </Reveal>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default FAQPage;
